import React, { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import { showNotification, getStudentId } from "../../utils";
import "./register_form.css";
import "react-notifications-component/dist/theme.css";
import Api from "../../api";
import Select from "../Select";
import { DOCUMENT_TYPES, LANGUAGES, ROL_OPTIONS } from "../../consts";

const CUSTOMER_ID_ERROR_MESSAGES = {
  400: "La nueva identificación debe ser diferente a la actual",
  404: "Usuario no encontrado",
  409: "La identificación ya está en uso",
};

// Reactivar cuando backend exponga GET/PUT /counters/{customerId} con DTO plano.
const COUNTERS_UI_ENABLED = true;

const COUNTER_FIELDS = [
  { key: "regularDaily", label: "Clases regulares (día)" },
  { key: "workshopDaily", label: "Talleres (día)" },
  { key: "regularWeekly", label: "Clases regulares (semana)" },
  { key: "regularAltWeekly", label: "Clases regulares alt. (semana)" },
  { key: "workshopWeekly", label: "Talleres (semana)" },
  { key: "regularMonthly", label: "Clases regulares (mes)" },
  { key: "regularAltMonthly", label: "Clases regulares alt. (mes)" },
];

const EMPTY_COUNTERS = {
  regularDaily: 0,
  workshopDaily: 0,
  regularWeekly: 0,
  regularAltWeekly: 0,
  workshopWeekly: 0,
  regularMonthly: 0,
  regularAltMonthly: 0,
};

const mapCountersToForm = (data) => ({
  regularDaily: data.regularDaily ?? 0,
  workshopDaily: data.workshopDaily ?? 0,
  regularWeekly: data.regularWeekly ?? 0,
  regularAltWeekly: data.regularAltWeekly ?? 0,
  workshopWeekly: data.workshopWeekly ?? 0,
  regularMonthly: data.regularMonthly ?? 0,
  regularAltMonthly: data.regularAltMonthly ?? 0,
});

const isStudentUser = (user) =>
  (user?.roles || []).includes("ROLE_STUDENT");

const UserInfoField = ({ label, value }) => (
  <div className="user-info-field">
    <span className="user-info-label">{label}</span>
    <span className="user-info-value">{value || "—"}</span>
  </div>
);

const UpdateUserForm = () => {
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({});
  const [userFound, setUserFound] = useState(false);
  const [isInfoEditable, setIsInfoEditable] = useState(false);
  const [isPasswordEditable, setIsPasswordEditable] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [newCustomerId, setNewCustomerId] = useState("");
  const [updatingCustomerId, setUpdatingCustomerId] = useState(false);
  const [isCountersEditable, setIsCountersEditable] = useState(false);
  const [countersForm, setCountersForm] = useState(null);
  const [countersNotFound, setCountersNotFound] = useState(false);
  const [countersLoadWarning, setCountersLoadWarning] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [savingCounters, setSavingCounters] = useState(false);

  const resetCountersState = () => {
    setIsCountersEditable(false);
    setCountersForm(null);
    setCountersNotFound(false);
    setCountersLoadWarning(false);
    setLoadingCounters(false);
    setSavingCounters(false);
  };

  const loadCounters = useCallback(async (customerId) => {
    setLoadingCounters(true);
    setCountersNotFound(false);
    setCountersLoadWarning(false);
    setCountersForm(null);
    try {
      const result = await Api.Counters.GetByCustomerId(customerId);
      if (!result.ok && result.status === 404) {
        setCountersNotFound(true);
        return;
      }
      if (result.existingButUnreadable) {
        setCountersForm({ ...EMPTY_COUNTERS });
        setCountersLoadWarning(true);
        return;
      }
      setCountersForm(mapCountersToForm(result.data));
    } catch (error) {
      showNotification("Error", error.message, "danger");
      setIsCountersEditable(false);
    } finally {
      setLoadingCounters(false);
    }
  }, []);

  useEffect(() => {
    if (
      COUNTERS_UI_ENABLED &&
      isCountersEditable &&
      userData?.customer_id
    ) {
      loadCounters(userData.customer_id);
    }
  }, [isCountersEditable, userData?.customer_id, loadCounters]);

  const handleSearchUser = async () => {
    try {
      const data = await Api.User.GetById({
        customer_id: formData.customer_id,
      });
      showNotification("¡Éxito!", "Usuario encontrado", "success");
      setUserFound(true);
      setUserData(data);
      setFormData({ customer_id: data.customer_id });
      setNewCustomerId("");
      setIsInfoEditable(false);
      setIsPasswordEditable(false);
      resetCountersState();
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const updateUserReq = async () => {
    const updatedData = {
      ...userData,
      ...formData,
    };

    const {
      contract_expiration,
      monthly_intensity,
      password,
      ...generalPayload
    } = updatedData;

    if (
      !generalPayload.first_name ||
      !generalPayload.roles ||
      !generalPayload.customer_id ||
      !generalPayload.paternal_last_name
    ) {
      showNotification("Error", "Campos requeridos faltantes", "danger");
      return;
    }

    try {
      await Api.User.Update(generalPayload);
      showNotification("¡Éxito!", "Usuario actualizado", "success");
      setUserData({ ...userData, ...formData });
      setFormData({ customer_id: generalPayload.customer_id });
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const updatePasswordReq = async () => {
    if (!passwordData.password) {
      showNotification("Error", "La contraseña no puede estar vacía", "danger");
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      showNotification("Error", "Las contraseñas no coinciden", "danger");
      return;
    }

    try {
      await Api.User.UpdatePassword({
        customer_id: userData.customer_id,
        password: passwordData.password,
      });
      showNotification("¡Éxito!", "Contraseña actualizada", "success");
      setPasswordData({ password: "", confirmPassword: "" });
      setIsPasswordEditable(false);
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const handleUpdateCustomerId = async () => {
    const trimmedNewId = newCustomerId.trim();
    if (!trimmedNewId) {
      showNotification("Error", "Ingrese la nueva identificación", "danger");
      return;
    }

    if (trimmedNewId === userData.customer_id) {
      showNotification(
        "Error",
        CUSTOMER_ID_ERROR_MESSAGES[400],
        "danger"
      );
      return;
    }

    setUpdatingCustomerId(true);
    try {
      const result = await Api.User.UpdateCustomerId({
        current_customer_id: userData.customer_id,
        new_customer_id: trimmedNewId,
      });

      if (!result.ok) {
        const message =
          CUSTOMER_ID_ERROR_MESSAGES[result.status] || result.message;
        showNotification("Error", message, "danger");
        return;
      }

      const loggedInId = getStudentId();
      const changedOwnId =
        loggedInId && String(loggedInId) === String(userData.customer_id);

      if (changedOwnId) {
        showNotification(
          "Sesión finalizada",
          "Su identificación cambió. Inicie sesión de nuevo.",
          "warning"
        );
        Cookies.remove("token");
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
        return;
      }

      showNotification("¡Éxito!", "Identificación actualizada", "success");
      setUserData({ ...userData, customer_id: trimmedNewId });
      setFormData({ customer_id: trimmedNewId });
      setNewCustomerId("");
    } catch (error) {
      showNotification("Error", error.message, "danger");
    } finally {
      setUpdatingCustomerId(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCounterChange = (key, value) => {
    const parsed = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setCountersForm((prev) => ({ ...prev, [key]: parsed }));
  };

  const handleResetCountersToZero = () => {
    setCountersForm({ ...EMPTY_COUNTERS });
  };

  const handleSaveCounters = async () => {
    if (!countersForm || !userData?.customer_id) return;

    const payload = {};
    for (const { key } of COUNTER_FIELDS) {
      const raw = countersForm[key];
      if (raw === "" || raw === null || raw === undefined) {
        showNotification(
          "Error",
          "Todos los contadores deben ser números enteros",
          "danger"
        );
        return;
      }
      payload[key] = Number(raw);
    }

    setSavingCounters(true);
    try {
      const result = await Api.Counters.Update(userData.customer_id, payload);
      if (!result.ok) {
        showNotification("Error", result.message || "Error al guardar", "danger");
        return;
      }
      setCountersForm(mapCountersToForm(payload));
      setCountersLoadWarning(false);
      showNotification("¡Éxito!", "Contadores actualizados", "success");
    } catch (error) {
      showNotification("Error", error.message, "danger");
    } finally {
      setSavingCounters(false);
    }
  };

  const userIsStudent = userData && isStudentUser(userData);

  return (
    <div className="register-container-user_handle">
      <div className="form-container-user_handle">
        <h1>Modificar Usuario</h1>
        <form className="form-register">
          <div className="selectedUser">
            <h4>Ingrese la identificación del usuario que desea modificar</h4>
            <input
              type="text"
              name="customer_id"
              className="customer_id"
              placeholder="Identificación"
              value={formData.customer_id || ""}
              onChange={handleInputChange}
            />
            <button
              type="button"
              className="button-search_user "
              onClick={handleSearchUser}
            >
              Buscar Usuario
            </button>
          </div>

          {userFound && userData && (
            <div className="user-info-plain-text">
              <h4>Información del Usuario</h4>
              <div className="user-info-grid">
                <UserInfoField label="Primer Nombre" value={userData.first_name} />
                <UserInfoField
                  label="Segundo Nombre"
                  value={userData.middle_name}
                />
                <UserInfoField
                  label="Primer Apellido"
                  value={userData.paternal_last_name}
                />
                <UserInfoField
                  label="Segundo Apellido"
                  value={userData.maternal_last_name}
                />
                <UserInfoField
                  label="Identificación"
                  value={userData.customer_id}
                />
                <UserInfoField
                  label="Tipo de Documento"
                  value={userData.customer_id_type}
                />
                <UserInfoField label="Email" value={userData.email} />
                <UserInfoField
                  label="Fecha de Expiración del Contrato"
                  value={userData.contract_expiration}
                />
                <UserInfoField
                  label="Intensidad Mensual"
                  value={userData.monthly_intensity}
                />
                <UserInfoField label="Idioma" value={userData.language} />
                <UserInfoField
                  label="Roles"
                  value={(userData.roles || []).join(", ")}
                />
              </div>

              <div className="toggle-cards-container">
                <div
                  className={`toggle-card ${isInfoEditable ? "toggle-card-active" : ""}`}
                  onClick={() => setIsInfoEditable(!isInfoEditable)}
                >
                  <input
                    type="checkbox"
                    checked={isInfoEditable}
                    onChange={() => setIsInfoEditable(!isInfoEditable)}
                  />
                  <span>Información</span>
                </div>
                <div
                  className={`toggle-card ${isPasswordEditable ? "toggle-card-active" : ""}`}
                  onClick={() => setIsPasswordEditable(!isPasswordEditable)}
                >
                  <input
                    type="checkbox"
                    checked={isPasswordEditable}
                    onChange={() => setIsPasswordEditable(!isPasswordEditable)}
                  />
                  <span>Contraseña</span>
                </div>
                {COUNTERS_UI_ENABLED && userIsStudent && (
                  <div
                    className={`toggle-card ${isCountersEditable ? "toggle-card-active" : ""}`}
                    onClick={() => setIsCountersEditable((prev) => !prev)}
                  >
                    <input
                      type="checkbox"
                      checked={isCountersEditable}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        setIsCountersEditable(e.target.checked);
                      }}
                    />
                    <span>Contadores</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {isInfoEditable && userData && (
            <div className="user-info-edit">
              <h4>Modifique los campos necesarios</h4>
              <div className="user-info-edit-grid">
                <div>
                  Primer Nombre:
                  <input
                    type="text"
                    name="first_name"
                    placeholder="Primer Nombre"
                    value={formData.first_name ?? userData.first_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  Segundo Nombre:
                  <input
                    type="text"
                    name="middle_name"
                    placeholder="Segundo Nombre"
                    value={formData.middle_name ?? userData.middle_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  Primer Apellido:
                  <input
                    type="text"
                    name="paternal_last_name"
                    placeholder="Primer Apellido"
                    value={
                      formData.paternal_last_name ?? userData.paternal_last_name
                    }
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  Segundo Apellido:
                  <input
                    type="text"
                    name="maternal_last_name"
                    placeholder="Segundo Apellido"
                    value={
                      formData.maternal_last_name ?? userData.maternal_last_name
                    }
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  Tipo de Documento:
                  <Select
                    name="customer_id_type"
                    options={DOCUMENT_TYPES}
                    value={DOCUMENT_TYPES.find(
                      (option) =>
                        option.value ===
                        (formData.customer_id_type || userData.customer_id_type)
                    )}
                    onChange={(selected) => {
                      setFormData({
                        ...formData,
                        customer_id_type: selected.value,
                      });
                    }}
                  />
                </div>

                <div>
                  Email:
                  <input
                    type="email"
                    name="email"
                    placeholder="Correo Electrónico"
                    value={formData.email ?? userData.email}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  Idioma:
                  <Select
                    name="language"
                    options={LANGUAGES}
                    value={LANGUAGES.find(
                      (option) =>
                        option.value === (formData.language || userData.language)
                    )}
                    onChange={(selected) => {
                      setFormData({ ...formData, language: selected.value });
                    }}
                  />
                </div>

                <div>
                  Roles:
                  <Select
                    isMulti
                    name="roles"
                    options={ROL_OPTIONS}
                    value={ROL_OPTIONS.filter((option) =>
                      (formData.roles || userData.roles || []).includes(
                        option.value
                      )
                    )}
                    onChange={(selectedRoles) => {
                      setFormData({
                        ...formData,
                        roles: selectedRoles.map((role) => role.value),
                      });
                    }}
                  />
                </div>
              </div>

              <div className="customer-id-change-section">
                <h4>Cambiar identificación</h4>
                <p className="customer-id-current">
                  Identificación actual: <strong>{userData.customer_id}</strong>
                </p>
                <div className="customer-id-change-row">
                  <input
                    type="text"
                    name="new_customer_id"
                    placeholder="Nueva identificación"
                    value={newCustomerId}
                    onChange={(e) => setNewCustomerId(e.target.value)}
                  />
                  <button
                    type="button"
                    className="button-search_user"
                    onClick={handleUpdateCustomerId}
                    disabled={updatingCustomerId}
                  >
                    {updatingCustomerId ? "Actualizando…" : "Cambiar ID"}
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="button-search_user"
                onClick={updateUserReq}
              >
                Actualizar Información
              </button>
            </div>
          )}

          {isPasswordEditable && userData && (
            <div className="user-info-edit password-edit-section">
              <h4>Cambiar Contraseña</h4>
              <div>
                Nueva Contraseña:
                <input
                  type="password"
                  name="password"
                  placeholder="Nueva contraseña"
                  value={passwordData.password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, password: e.target.value })
                  }
                />
              </div>
              <div>
                Confirmar Contraseña:
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirmar contraseña"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                />
              </div>
              <button
                type="button"
                className="button-search_user"
                onClick={updatePasswordReq}
              >
                Actualizar Contraseña
              </button>
            </div>
          )}

          {COUNTERS_UI_ENABLED && isCountersEditable && userIsStudent && userData && (
            <div className="user-info-edit counters-edit-section">
              <h4>Contadores del estudiante</h4>
              {loadingCounters ? (
                <p className="counters-status-message">Cargando contadores…</p>
              ) : countersNotFound ? (
                <p className="counters-status-message">
                  Este estudiante no tiene contadores registrados. Puede
                  inicializarlos de forma masiva desde Admin → Operaciones →
                  Inicializar contadores.
                </p>
              ) : countersForm ? (
                <>
                  {countersLoadWarning && (
                    <p className="counters-status-message counters-load-warning">
                      No se pudieron leer los valores actuales del servidor.
                      Ingrese los contadores manualmente y guarde.
                    </p>
                  )}
                  <div className="user-info-edit-grid counters-edit-grid">
                    {COUNTER_FIELDS.map(({ key, label }) => (
                      <div key={key}>
                        {label}:
                        <input
                          type="number"
                          min="0"
                          step="1"
                          name={key}
                          value={countersForm[key]}
                          onChange={(e) =>
                            handleCounterChange(key, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="counters-actions">
                    <button
                      type="button"
                      className="button-search_user"
                      onClick={handleSaveCounters}
                      disabled={savingCounters}
                    >
                      {savingCounters ? "Guardando…" : "Guardar contadores"}
                    </button>
                    <button
                      type="button"
                      className="button-clear_fields"
                      onClick={handleResetCountersToZero}
                      disabled={savingCounters}
                    >
                      Reiniciar a cero
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default UpdateUserForm;
